import { api } from '../../../common/api/httpClient';
import type {
  PageResult, PortalMyTaskCounts, PortalTransferTarget,
  PortalAttachmentUploadIntent,
  PortalWorkItemDetail, PortalTaskDetail, PortalWorkItemListItem,
  PortalUploadedAttachment,
  PortalInitiatedInstanceListItem,
  PortalInitiatedInstanceDetail,
  PortalTaskComment,
} from '../model/myTasks';
import { buildMyTasksRoute } from '../runtime/myTasksRoute';

export const MY_TASKS_PAGE_SIZE = 10;
export const INSTANCE_COMMENTS_PAGE_SIZE = 20;

export type MyTasksListQuery = {
  category: string;
  keyword?: string;
  processDefinitionName?: string;
  quickApprovalOnly?: boolean;
  page?: number;
  pageSize?: number;
};

function query(path: string, values: Record<string, unknown>) {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  });
  const suffix = params.toString();
  return suffix ? `${path}?${suffix}` : path;
}

function json(method: string, body?: unknown): RequestInit {
  return { method, body: body === undefined ? undefined : JSON.stringify(body) };
}

async function sha256(file: File) {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(digest))
    .map(value => value.toString(16).padStart(2, '0')).join('');
}

export function workItemListPath(values: MyTasksListQuery) {
  if (values.category === 'initiated') {
    return query('/api/portal/initiated-instances', {
      keyword: values.keyword, processDefinitionName: values.processDefinitionName,
      page: values.page, pageSize: values.pageSize,
    });
  }
  return query('/api/portal/work-items', values);
}

function initiatedView(item: PortalInitiatedInstanceListItem): PortalWorkItemListItem {
  return {
    taskId: item.initiatedCardPublicId,
    initiatedCardPublicId: item.initiatedCardPublicId,
    processInstancePublicId: item.processInstancePublicId,
    category: 'initiated', sourceSystem: item.sourceSystem,
    title: item.title, processDefinitionName: item.processDefinitionName,
    taskName: item.processDefinitionName, status: item.status,
    sourceState: item.sourceState, sourceVersion: item.sourceVersion,
    initiator: item.initiator, initiatorAvatarUrl: item.initiatorAvatarUrl,
    currentStage: item.processDefinitionName, receivedAt: item.receivedAt,
    updatedAt: item.updatedAt, summary: item.summary,
    urgent: false, unread: false,
    relatedObject: {
      name: item.businessObjectType, code: item.businessObjectPublicId,
      routePath: buildMyTasksRoute('initiated', item.initiatedCardPublicId),
    },
    summaryItems: item.summaryItems, quickApprovable: false,
    runtimeTaskVersion: 0,
  };
}

export const myTasksApi = {
  processDefinitionNames(category: string) {
    return api<string[]>(category === 'initiated'
      ? '/api/portal/initiated-instances/process-definition-names'
      : query('/api/portal/work-items/process-definition-names', { category }));
  },
  instanceComments(cardId: string, page = 1,
    options: Readonly<{ signal?: AbortSignal }> = {}) {
    return api<PageResult<PortalTaskComment>>(query(
      `/api/portal/initiated-instances/${cardId}/comments`,
      { page, pageSize: INSTANCE_COMMENTS_PAGE_SIZE }), { signal: options.signal });
  },
  async list(values: MyTasksListQuery) {
    if (values.category === 'initiated') {
      const result = await api<PageResult<PortalInitiatedInstanceListItem>>(
        workItemListPath(values));
      return { ...result, items: result.items.map(initiatedView) };
    }
    return api<PageResult<PortalWorkItemListItem>>(workItemListPath(values));
  },
  async counts() {
    const [tasks, instances] = await Promise.all([
      api<Omit<PortalMyTaskCounts, 'initiated'>>('/api/portal/work-items/counts'),
      api<{ initiated: number }>('/api/portal/initiated-instances/counts'),
    ]);
    return { ...tasks, initiated: instances.initiated };
  },
  async detail(workItemId: string, category: 'initiated' | 'work-item' = 'work-item',
    options: Readonly<{ signal?: AbortSignal }> = {}) {
    if (category === 'initiated') {
      const detail = await api<{
        summary: PortalInitiatedInstanceListItem;
        processInstanceId: string;
        pageRoute: string | null;
        processInitiationViewContext: PortalInitiatedInstanceDetail['processInitiationViewContext'];
        traceNodes: PortalInitiatedInstanceDetail['traceNodes'];
        commentCount: number;
      }>(`/api/portal/initiated-instances/${workItemId}`, { signal: options.signal });
      return {
        summary: initiatedView(detail.summary),
        processInstanceId: detail.processInstanceId,
        pageRoute: detail.pageRoute,
        flowPageContext: null,
        processInitiationViewContext: detail.processInitiationViewContext,
        traceNodes: detail.traceNodes,
        commentCount: detail.commentCount,
        availableActions: [], attachments: [], comments: [],
      } satisfies PortalInitiatedInstanceDetail;
    }
    return api<PortalTaskDetail>(`/api/portal/work-items/${workItemId}`, {
      signal: options.signal,
    });
  },
  action(workItemId: string, body: {
    actionCode: string;
    expectedTaskVersion: number;
    opinion?: string;
    targetUserPublicId?: string;
    opinionAttachmentPublicIds?: string[];
    operationId: string;
  }, options: Readonly<{ signal?: AbortSignal }> = {}) {
    return api<PortalWorkItemDetail>(
      `/api/portal/work-items/${workItemId}/actions`, {
        ...json('POST', body), signal: options.signal,
      });
  },
  async uploadAttachment(
    workItemId: string,
    actionCode: string,
    expectedTaskVersion: number,
    file: File,
  ) {
    const mimeType = file.type || 'application/octet-stream';
    const fileHash = await sha256(file);
    const intent = await api<PortalAttachmentUploadIntent>(query(
      `/api/portal/work-items/${workItemId}/attachments/upload-intents`,
      { actionCode, expectedTaskVersion },
    ), json('POST', {
      fileName: file.name,
      fileHash,
      mimeType,
      sizeBytes: file.size,
    }));
    const uploadResponse = await fetch(intent.uploadUrl, {
      method: 'PUT',
      headers: intent.headers,
      body: file,
      credentials: 'omit',
    });
    if (!uploadResponse.ok) {
      throw new Error(`OSS 上传失败（HTTP ${uploadResponse.status}）`);
    }
    return api<PortalUploadedAttachment>(query(
      `/api/portal/work-items/${workItemId}/attachments/complete`,
      { actionCode, expectedTaskVersion },
    ), json('POST', {
      fileAssetPublicId: intent.fileAssetPublicId,
      objectKey: intent.objectKey,
    }));
  },
  comment(workItemId: string, content: string, options: Readonly<{
    category?: 'initiated' | 'work-item';
    parentCommentId?: string;
    idempotencyKey?: string;
  }> = {}) {
    const base = options.category === 'initiated'
      ? '/api/portal/initiated-instances' : '/api/portal/work-items';
    return api<PortalTaskComment>(`${base}/${workItemId}/comments`, json('POST', {
      idempotencyKey: options.idempotencyKey ?? crypto.randomUUID(),
      parentCommentId: options.parentCommentId ?? null, content,
    }));
  },
  deleteComment(workItemId: string, commentId: string, options: Readonly<{
    category?: 'initiated' | 'work-item';
  }> = {}) {
    const base = options.category === 'initiated'
      ? '/api/portal/initiated-instances' : '/api/portal/work-items';
    return api<void>(`${base}/${workItemId}/comments/${commentId}`, { method: 'DELETE' });
  },
  markRead(workItemId: string, options: Readonly<{ signal?: AbortSignal }> = {}) {
    return api<void>(`/api/portal/work-items/${workItemId}/read`, {
      ...json('PUT'), signal: options.signal,
    });
  },
  markAllCcRead() {
    return api<{ updated: number }>('/api/portal/work-items/read-all?category=cc', json('PUT'));
  },
  transferTargets(keyword?: string) {
    return api<PortalTransferTarget[]>(query('/api/portal/work-items/transfer-targets', { keyword }));
  },
};
